// Verifies a Keycloak JWT signature against the realm JWKS. The JWKS is memoised per
// issuer at module level so the certs endpoint isn't hit on every request.
import {
  createRemoteJWKSet,
  jwtVerify,
  type JWTPayload,
  type JWTVerifyGetKey,
} from "jose";

// Keyed by issuer, not a single "last one seen" slot: Catalog validates two realms — `odys`
// for the shared DMC session, `catalog` for its own agency session — and they alternate
// request after request in the same process. One slot would evict on every alternation and
// refetch the realm certs each time.
const jwksByIssuer = new Map<string, JWTVerifyGetKey>();

function getJwks(issuer: string): JWTVerifyGetKey {
  const cached = jwksByIssuer.get(issuer);
  if (cached) {
    return cached;
  }
  const jwks = createRemoteJWKSet(
    new URL(`${issuer}/protocol/openid-connect/certs`),
  );
  jwksByIssuer.set(issuer, jwks);
  return jwks;
}

export type TokenErrorReason =
  | "expired"
  | "invalid-signature"
  | "claim-invalid"
  | "key-unavailable"
  | "unknown";

// Maps a jose verification failure to a coarse reason so logs name the real cause
// instead of always blaming the signature — an expired token, a JWKS outage and a
// forged signature are very different incidents. `code` is jose's stable identifier.
export function classifyTokenError(error: unknown): TokenErrorReason {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  switch (code) {
    case "ERR_JWT_EXPIRED":
      return "expired";
    case "ERR_JWS_SIGNATURE_VERIFICATION_FAILED":
      return "invalid-signature";
    case "ERR_JWT_CLAIM_VALIDATION_FAILED":
      return "claim-invalid";
    case "ERR_JWKS_NO_MATCHING_KEY":
    case "ERR_JWKS_TIMEOUT":
    case "ERR_JWKS_INVALID":
    case "ERR_JWKS_MULTIPLE_MATCHING_KEYS":
      return "key-unavailable";
    default:
      return "unknown";
  }
}

/**
 * Whether a verification failure genuinely condemns the token, as opposed to saying we could
 * not check it. The single answer to "does this failure justify signing the user out?".
 *
 * The default has to be "no": `key-unavailable` only covers jose's `ERR_JWKS_*` codes, so the
 * shapes a real Keycloak outage takes — a non-200 from the certs endpoint (`ERR_JOSE_GENERIC`)
 * or a refused connection (a bare `TypeError`) — both classify as `unknown`, as does a missing
 * `KEYCLOAK_ISSUER` (`no-issuer`). Listing the fatal cases instead of the transient ones is
 * what keeps an infra hiccup or a bad env var from signing every user out of every app on
 * `.odys.travel` at once.
 *
 * Every caller that verifies a token to decide whether a session is alive must branch on this
 * rather than on `payload === null`. Collapsing it to a boolean is how one path ends up with
 * the fix and another with the mass logout — `session-refresh.ts` had it, the proxy gate and
 * the SSR user lookup did not, and a JWKS blip there became an endless rotate-and-replay loop
 * (saas-issues#2365).
 */
export function isDeadTokenReason(
  reason: TokenErrorReason | "no-issuer",
): boolean {
  return (
    reason === "expired" ||
    reason === "invalid-signature" ||
    reason === "claim-invalid"
  );
}

export type VerifyTokenResult =
  | { payload: JWTPayload }
  | { payload: null; reason: TokenErrorReason | "no-issuer" };

/**
 * Verifies signature + issuer and reports WHY it failed.
 *
 * The reason is load-bearing, not decoration: `key-unavailable` means the certs endpoint
 * was unreachable, which is an infra hiccup and must NOT sign the user out, whereas
 * `invalid-signature` must. Callers that collapse this to a boolean reintroduce the
 * mass-logout failure mode.
 *
 * `opts.issuer` names the realm to verify against, for the apps that read more than one
 * (Catalog: `odys` for the shared DMC session, `catalog` for its agency session). Omitted,
 * it falls back to `KEYCLOAK_ISSUER` — the single-realm apps are untouched.
 */
export async function verifyTokenWithReason(
  token: string,
  opts?: { audience?: string; issuer?: string },
): Promise<VerifyTokenResult> {
  const issuer = opts?.issuer || process.env.KEYCLOAK_ISSUER;
  if (!issuer) {
    return { payload: null, reason: "no-issuer" };
  }

  try {
    const { payload } = await jwtVerify(token, getJwks(issuer), {
      issuer,
      audience: opts?.audience,
      // Pin RS256 to foreclose any algorithm-substitution attempt.
      algorithms: ["RS256"],
    });
    return { payload };
  } catch (error) {
    const reason = classifyTokenError(error);
    console.warn(`[verifyToken] token verification failed: ${reason}`);
    return { payload: null, reason };
  }
}

export async function verifyToken(
  token: string,
  opts?: { audience?: string; issuer?: string },
): Promise<JWTPayload | null> {
  return (await verifyTokenWithReason(token, opts)).payload;
}
